"use client"

import { useState, useEffect } from "react"
import Header from "./components/Header"
import DogForm from "./components/DogForm"
import RecommendationCard from "./components/RecommendationCard"
import HomemadeAlternative from "./components/HomemadeAlternative"
import FeedingGuidelines from "./components/FeedingGuidelines"
import Footer from "./components/Footer"
import ThemeToggle from "./components/ThemeToggle"
import { getRecommendations } from "./services/openaiService"
import { getDogBreedInfo } from "./services/dogApiService"
import { savePreferences, getPreferences } from "./services/storageService"
import "./styles/App.css"

function App() {
  const [dogInfo, setDogInfo] = useState({
    breed: "",
    age: "",
    weight: "",
    healthConditions: [],
  })

  const [recommendations, setRecommendations] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [breedInfo, setBreedInfo] = useState(null)
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [shareUrl, setShareUrl] = useState("")

  // Load saved preferences and theme on initial render
  useEffect(() => {
    const savedPreferences = getPreferences()
    const savedTheme = localStorage.getItem("theme")
    
    if (savedPreferences) {
      setDogInfo(savedPreferences)
    }
    
    if (savedTheme === "dark") {
      setIsDarkMode(true)
      document.documentElement.setAttribute("data-theme", "dark")
    }
  }, [])

  // Handle theme toggle
  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode)
    const newTheme = !isDarkMode ? "dark" : "light"
    document.documentElement.setAttribute("data-theme", newTheme)
    localStorage.setItem("theme", newTheme)
  }

  // Handle share functionality
  const handleShare = () => {
    const url = window.location.href
    setShareUrl(url)
    setShowShareModal(true)
  }

  const handleFormSubmit = async (formData) => {
    setLoading(true)
    setError(null)

    try {
      // Save user preferences
      savePreferences(formData)
      setDogInfo(formData)

      // Get breed-specific information
      const breedData = await getDogBreedInfo(formData.breed)
      setBreedInfo(breedData)

      // Get food recommendations
      const recommendationsData = await getRecommendations(formData, breedData)
      setRecommendations(recommendationsData)
    } catch (err) {
      setError("Failed to get recommendations. Please try again.")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="app">
      <ThemeToggle isDarkMode={isDarkMode} toggleTheme={toggleTheme} />
      <Header />
      <main className="app-content">
        <DogForm onSubmit={handleFormSubmit} initialData={dogInfo} />

        {loading && <div className="loading">Fetching recommendations...</div>}

        {error && <div className="error">{error}</div>}

        {recommendations && !loading && (
          <div className="recommendations-container">
            <div className="recommendations-header">
              <div className="dog-info-summary">
                <h2>🐶 Dog Breed: {dogInfo.breed}</h2>
                <h3>
                  📅 Age: {dogInfo.age} {dogInfo.age === "1" ? "Year" : "Years"}
                </h3>
                {dogInfo.weight && <h3>⚖️ Weight: {dogInfo.weight} kg</h3>}
              </div>
              <button className="share-button" onClick={handleShare}>
                Share Recommendations
              </button>
            </div>

            <h2 className="section-title">🍖 Recommended Dog Foods:</h2>
            <div className="recommendations-grid">
              {recommendations.foods.map((food, index) => (
                <RecommendationCard key={index} food={food} index={index} />
              ))}
            </div>

            {recommendations.homemade && <HomemadeAlternative alternatives={recommendations.homemade} />}

            {recommendations.feedingGuidelines && <FeedingGuidelines guidelines={recommendations.feedingGuidelines} />}
          </div>
        )}
      </main>
      <Footer />

      {/* Share Modal */}
      {showShareModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Share Recommendations</h3>
            <div className="share-options">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="share-url-input"
              />
              <button
                className="copy-button"
                onClick={() => {
                  navigator.clipboard.writeText(shareUrl)
                  alert("Link copied to clipboard!")
                }}
              >
                Copy Link
              </button>
            </div>
            <button
              className="close-modal-button"
              onClick={() => setShowShareModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default App

